/**
 * API Request Helper Utilities
 * 
 * Provides utilities for preparing API request payloads using OpenAPI generated types,
 * automatically filtering out empty optional fields based on TypeScript type definitions.
 */

import type { components } from '../types/api-generated';

/**
 * Configuration for field filtering
 */
export interface FilterFieldsConfig {
  /** Fields that should always be included (required fields) */
  requiredFields: string[];
  /** Fields that should be excluded from filtering (always included if present) */
  excludeFromFiltering?: string[];
  /** Custom validation function for specific fields */
  customValidation?: Record<string, (value: any) => boolean>;
}



/**
 * Checks if a value is considered "empty" and should be filtered out
 */
function isEmptyValue(value: any): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  
  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }
  
  return false;
}

/**
 * Type-safe function to filter out empty optional fields from OpenAPI request types
 * 
 * @param data - The form data object to filter (should match OpenAPI request type)
 * @returns Filtered object with only populated fields, maintaining type safety
 * 
 * @example
 * ```typescript
 * const formData: components['schemas']['CreateOrganizationRequest'] = {
 *   name: 'Test Org',
 *   email: 'test@example.com',
 *   contactNumber: '',           // Optional - will be filtered out
 *   notificationEmail: '',       // Optional - will be filtered out
 *   notificationContactName: 'John' // Optional - will be included
 * };
 * 
 * const filtered = filterEmptyOptionalFields(formData);
 * // Result: { name: 'Test Org', email: 'test@example.com', notificationContactName: 'John' }
 * ```
 */
export function filterEmptyOptionalFields<T extends Record<string, any>>(
  data: T
): T {
  const filtered = {} as any;
  
  Object.keys(data).forEach(key => {
    const value = data[key];
    
    // Always include if not empty
    if (!isEmptyValue(value)) {
      filtered[key] = typeof value === 'string' ? value.trim() : value;
    }
  });
  
  return filtered;
}

/**
 * Legacy function for backward compatibility - use filterEmptyOptionalFields instead
 * @deprecated Use filterEmptyOptionalFields for type-safe filtering
 */
export function filterEmptyFields<T extends Record<string, any>>(
  data: T,
  config: FilterFieldsConfig
): Partial<T> {
  const filtered: any = {};
  
  // Always include required fields (even if empty - let validation handle it)
  config.requiredFields.forEach(field => {
    if (field in data) {
      filtered[field] = data[field];
    }
  });
  
  // Process optional fields
  Object.keys(data).forEach(key => {
    // Skip if already processed as required field
    if (config.requiredFields.includes(key)) {
      return;
    }
    
    // Always include if excluded from filtering
    if (config.excludeFromFiltering?.includes(key)) {
      filtered[key] = data[key];
      return;
    }
    
    const value = data[key];
    
    // Apply custom validation if provided
    if (config.customValidation?.[key]) {
      if (config.customValidation[key](value)) {
        filtered[key] = typeof value === 'string' ? value.trim() : value;
      }
      return;
    }
    
    // Default filtering: include if not empty
    if (!isEmptyValue(value)) {
      filtered[key] = typeof value === 'string' ? value.trim() : value;
    }
  });
  
  return filtered;
}

/**
 * Type-safe convenience functions for OpenAPI request types
 */

/**
 * Filter empty optional fields for organization creation requests
 */
export function filterCreateOrganizationRequest(
  data: components['schemas']['CreateOrganizationRequest']
): components['schemas']['CreateOrganizationRequest'] {
  return filterEmptyOptionalFields(data);
}

/**
 * Filter empty optional fields for organization update requests
 */
export function filterUpdateOrganizationRequest(
  data: components['schemas']['UpdateOrganizationRequest']
): components['schemas']['UpdateOrganizationRequest'] {
  return filterEmptyOptionalFields(data);
}

/**
 * Filter empty optional fields for certificate creation requests
 */
export function filterCreateCertificateRequest(
  data: components['schemas']['CreateCertificateRequest']
): components['schemas']['CreateCertificateRequest'] {
  return filterEmptyOptionalFields(data);
}

/**
 * Legacy convenience functions for backward compatibility
 * @deprecated Use the type-safe functions above instead
 */
export const FORM_CONFIGS = {
  organization: {
    requiredFields: ['name', 'email'],
  } as FilterFieldsConfig,
  
  product: {
    requiredFields: ['productName', 'organizationId'],
  } as FilterFieldsConfig,
  
  certificate: {
    requiredFields: ['certificateType', 'commonName', 'createdBy', 'environment', 'expiryDate', 'keyUsage'],
    customValidation: {
      keyUsage: (value: any[]) => Array.isArray(value) && value.length > 0,
    },
  } as FilterFieldsConfig,
  
  patUser: {
    requiredFields: ['username', 'email', 'givenName', 'familyName', 'groups'],
    customValidation: {
      groups: (value: any[]) => Array.isArray(value) && value.length > 0,
    },
  } as FilterFieldsConfig,
  
  patGroup: {
    requiredFields: ['groupName'],
  } as FilterFieldsConfig,
} as const;

/** @deprecated Use filterCreateOrganizationRequest instead */
export function filterOrganizationFields<T extends Record<string, any>>(data: T): Partial<T> {
  return filterEmptyFields(data, FORM_CONFIGS.organization);
}

/** @deprecated Use type-safe functions instead */
export function filterProductFields<T extends Record<string, any>>(data: T): Partial<T> {
  return filterEmptyFields(data, FORM_CONFIGS.product);
}

/** @deprecated Use filterCreateCertificateRequest instead */
export function filterCertificateFields<T extends Record<string, any>>(data: T): Partial<T> {
  return filterEmptyFields(data, FORM_CONFIGS.certificate);
}

/**
 * Utility to prepare API request payload by filtering empty fields
 * and optionally transforming field names
 * 
 * @param data - The form data to prepare
 * @param config - Field filtering configuration
 * @param fieldMapping - Optional mapping to transform field names (formField -> apiField)
 * @returns Prepared payload for API request
 * 
 * @example
 * ```typescript
 * const payload = prepareApiPayload(formData, FORM_CONFIGS.organization, {
 *   contactNumber: 'contact_number',
 *   notificationEmail: 'notification_email'
 * });
 * ```
 */
export function prepareApiPayload<T extends Record<string, any>>(
  data: T,
  config: FilterFieldsConfig,
  fieldMapping?: Record<string, string>
): Record<string, any> {
  const filtered = filterEmptyFields(data, config);
  
  if (!fieldMapping) {
    return filtered;
  }
  
  const mapped: Record<string, any> = {};
  
  Object.keys(filtered).forEach(key => {
    const mappedKey = fieldMapping[key] || key;
    mapped[mappedKey] = filtered[key];
  });
  
  return mapped;
}